import { hashKey, useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SortDirection, Transit, TransitGroup, TransitsCriteria } from '@e-origin/shared';

import { queryClient } from '@hooks/queryClient';
import { showErrorToast } from '@hooks/showErrorToast';
import { request } from '@utils';

export const defaultAdminTransitsCriteria: TransitsCriteria = {
  filters: { filterByUsers: [], filterByNotifications: [], searchQuery: '', group: TransitGroup.DEPARTURE },
  pagination: { page: 1, size: 10, direction: 1, searchToken: '', currentToken: '' },
  sorting: { field: 'counter', direction: SortDirection.DESC },
};

const parseUrlToCriteria = (searchParams: URLSearchParams): TransitsCriteria => {
  const filters: TransitsCriteria['filters'] = {
    filterByUsers: [],
    filterByNotifications: [],
    group: TransitGroup.DEPARTURE,
  };

  filters.searchQuery = searchParams.get('searchQuery') || '';
  filters.group = (searchParams.get('group') as TransitGroup) || TransitGroup.DEPARTURE;

  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  const sortField = searchParams.get('sortField') || 'counter';
  const sortDir = (searchParams.get('sortDir') || SortDirection.DESC) as SortDirection;

  return {
    filters,
    pagination: { page, size },
    sorting: { field: sortField, direction: sortDir },
  };
};

const criteriaToSearchParams = (criteria: TransitsCriteria): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (criteria.filters.searchQuery) {
    searchParams.set('searchQuery', criteria.filters.searchQuery);
  }

  searchParams.set('group', criteria.filters.group);

  searchParams.set('page', criteria.pagination.page.toString());
  searchParams.set('size', criteria.pagination.size.toString());

  searchParams.set('sortField', criteria.sorting.field);
  searchParams.set('sortDir', criteria.sorting.direction);

  return searchParams;
};

export const getInitialTransitCriteria = (searchParams: URLSearchParams): TransitsCriteria => {
  if (searchParams.toString()) {
    return parseUrlToCriteria(searchParams);
  }
  return defaultAdminTransitsCriteria;
};

export const useTransitCriteriaUrlSync = (criteria?: TransitsCriteria) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!criteria) return;

    const currentCriteria = parseUrlToCriteria(searchParams);
    if (isEqual(currentCriteria, criteria)) return;

    const newParams = criteriaToSearchParams(criteria);
    setSearchParams(newParams, { replace: true });
  }, [criteria]);
};

const fetchAdminTransits = async (criteria: TransitsCriteria): Promise<{ list: Transit[]; total: number }> => {
  try {
    const {
      data: { transitsList, totalItems },
    } = await request({
      path: `admin/transits`,
      method: 'POST',
      authenticate: true,
      dataObject: { criteria },
    });
    return { list: transitsList.map((b) => ({ ...b, createdAt: new Date(b.createdAt), updatedAt: new Date(b.updatedAt) })), total: totalItems };
  } catch (error) {
    showErrorToast(error, 'Failed to fetch admin transits');
    return { list: [], total: 0 };
  }
};

export const useAdminTransits = (criteria: TransitsCriteria = defaultAdminTransitsCriteria) => {
  return useQuery({
    queryKey: ['admin-transits', hashKey([criteria])],
    queryFn: () => fetchAdminTransits(criteria),
  });
};

export const prefetchAdminTransits = (criteria: TransitsCriteria = defaultAdminTransitsCriteria) =>
  queryClient.prefetchQuery({
    queryKey: ['admin-transits', hashKey([criteria])],
    queryFn: () => fetchAdminTransits(criteria),
  });
